import type { BusinessUnitStanding } from '@clockwise/clients/business-units';
import type { MeImpersonation, User } from '@clockwise/clients/users';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from './api';
import { queryKeys } from './query-keys';

// 30 seconds. Short enough that a revoked permission stops appearing
// granted within a minute of the admin saving the role change, long enough
// to avoid hammering /api/users/me on every nav.
export const ME_STALE_MS = 30_000;

export const meQueryOptions = {
  queryKey: queryKeys.users.me(),
  queryFn: () => usersApi.me(),
  staleTime: ME_STALE_MS,
  retry: false,
};

/** The `/api/users/me` payload. */
export type Me = Awaited<ReturnType<typeof meQueryOptions.queryFn>>;

/** Resolved permission-id set from a `me` payload (empty while absent). */
export function permissionSetFor(me: Me | undefined): Set<string> {
  return new Set((me?.related?.permissions ?? []).map((e) => e.permission));
}

/**
 * The calling user's domain `users` row + their resolved permission ids,
 * loaded once at the authenticated layout boundary and cached. Modules read
 * the cached value via this hook (and the `useHasPermission` hook in
 * `permissions.ts`).
 *
 * Returns `null` user + empty `permissions` while loading or if no users row
 * is linked to the session. Surface a fallback UI in that case.
 */
export function useMe(): {
  user: User | null;
  permissions: Set<string>;
  /** The caller's own role's grants WITH their scopes. Access checks read
   *  `permissions`; this is for the rare screen that must know a scope (an
   *  import the server reserves for scope All). */
  permissionEntries: readonly { permission: string; scope?: string | null }[];
  impersonation: MeImpersonation;
  /**
   * The caller's system-role NAME, or `null` while loading and when the id on
   * their row resolves to no role.
   *
   * ALMOST NOTHING SHOULD READ THIS. Access is decided by `permissions`, which
   * is what a tenant admin can actually edit; a gate on the role's name breaks
   * the moment somebody renames it or builds a second role that ought to have
   * the same reach. It exists for the one case a permission cannot express —
   * a preview held back to Administrator while it is being checked — and the
   * gate is expected to be deleted rather than to grow. See
   * {@link useIsAdministrator}.
   */
  systemRoleName: string | null;
  /** The business units the caller can act in, with their role in each. */
  businessUnits: BusinessUnitStanding[];
  loading: boolean;
  missing: boolean;
} {
  const q = useQuery(meQueryOptions);
  const user = q.data?.data ?? null;
  const permissions = permissionSetFor(q.data);
  const impersonation = q.data?.related?.impersonation ?? { impersonating: false };
  return {
    user,
    permissions,
    permissionEntries: q.data?.related?.permissions ?? [],
    impersonation,
    systemRoleName: q.data?.related?.systemRoleName ?? null,
    businessUnits: q.data?.related?.businessUnits ?? [],
    loading: q.isLoading,
    missing: !q.isLoading && !user,
  };
}

/**
 * The name the tenant-bootstrap seeder gives the role that holds the whole
 * permission registry — see `SEED_SYSTEM_ROLES` in the API's tenant service.
 * Hardcoded TEXT, deliberately: this is a temporary gate, and an id would have
 * to be looked up per tenant for a check that is meant to be deleted.
 */
export const ADMINISTRATOR_ROLE = 'Administrator';

/**
 * Is the caller an Administrator, by the NAME of their system role?
 *
 * The gate for features that are built but not yet meant for everyone. It is
 * deliberately cruder than a permission: there is no `customer.budget.actuals`
 * id to grant, no role editor entry to find, and nothing to back out of four
 * environments when the preview opens up — the gate is one `if` and its
 * removal is one diff.
 *
 * FALSE WHILE `me` IS IN FLIGHT, like every other gate in this app. A preview
 * control that flashes in and then disappears is worse than one that appears a
 * beat late, and `loading` is there for callers that want to withhold the whole
 * surface instead.
 *
 * ONE NAME, and not also `Super Admin`. That second seeded role is
 * Administrator plus impersonation, so on the face of it it belongs here — but
 * a tenant's roles are its own, and the live tenant's is spelt
 * `Super Admistrator`. Writing that typo into the app would enshrine it;
 * writing the correct spelling would add a branch that matches nobody and
 * reads as though it does. Either is worse than the plain rule: a Super Admin
 * who needs this can hold the Administrator role, and the gate is temporary
 * anyway.
 */
export function useIsAdministrator(): boolean {
  return useMe().systemRoleName === ADMINISTRATOR_ROLE;
}
